import * as React from 'react';
import Paper from '@mui/material/Paper';
import {
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControl,
  Tooltip,
  Switch,
  InputAdornment,
  FormControlLabel,
} from '@mui/material';
import { AddShoppingCart, Delete, Discount, Search, Close, InfoOutlined } from '@mui/icons-material';
import { List, getScrollbarSize } from 'react-window';
import { useEffect } from 'react';
import API from '../services/api';

const normalizeText = (value) => {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
};

const buildSearchText = (code, desc, formatText = '') =>
  normalizeText(`${code || ''} ${desc || ''} ${formatText || ''}`);

const getSafeNumber = (value) => {
  if (value === undefined || value === null || value === '') return 0;
  const num = parseFloat(value);
  return isNaN(num) ? 0 : parseFloat(num.toFixed(2));
};

const ROW_HEIGHT = 56;

const COL_WIDTHS = {
  index: 60,
  code: 160,
  description: 425,
  quantity: 150,
  format: 160,
  price: 160,
  bonus: 160,
  final: 160,
  actions: 100,
};

const getGridCellSx = (align = 'left') => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start',
  px: 1,
  minWidth: 0,
  height: '100%',
  boxSizing: 'border-box',
  fontSize: '0.9rem'
});

const getHeaderCellSx = (align = 'left') => ({
  ...getGridCellSx(align),
  fontWeight: 'bold',
  color: 'white',
  py: 1,
  whiteSpace: 'nowrap',
});

// Memoized rows reduce rerenders when only one row changes.
const ProductRow = React.memo(function ProductRow({
  style,
  ariaAttributes,
  gridTemplateColumns,
  displayIdx,
  row,
  rowIndex,
  hasPromotions,
  showPromoPriceWarning,
  formats,
  formatsById,
  resolveFormatId,
  resolveFormatLabel,
  onQuantityChange,
  onFormatChange,
  onListPriceChange,
  onBonusPriceChange,
  onPriceChange,
  onTextFieldBlur,
  onRemove,
  onAddPromo,
  pendingContext,
}) {
  const formatValue = resolveFormatId(row) || '';
  const formatLabel = formatValue
    ? (formatsById.get(String(formatValue))?.name || resolveFormatLabel(row))
    : resolveFormatLabel(row);
  const finalPlaceholder =
    row.isBonusPriceModified && row.bonusPrice
      ? row.bonusPrice.toString()
      : row.listPrice?.toString() || '0';
  const pendingQty = Number(pendingContext?.consolidated_pending_quantity || 0);
  const pendingOrders = Number(pendingContext?.pending_orders_count || 0);
  const pendingFormats = Array.isArray(pendingContext?.formats) ? pendingContext.formats.filter(Boolean) : [];
  const pendingByOrder = Array.isArray(pendingContext?.by_order) ? pendingContext.by_order : [];
  const hasPendingHint = pendingQty > 0;

  return (
    <Box
      component="div"
      style={style}
      {...ariaAttributes}
      sx={{
        display: 'grid',
        gridTemplateColumns,
        alignItems: 'center',
        height: ROW_HEIGHT,
        borderBottom: '1px dashed rgba(0, 0, 0, 0.12)',
      }}
    >
      <Box sx={getGridCellSx('left')}>
        <Box
          sx={{
            display: 'inline-block',
            minWidth: '24px',
            px: 1,
            py: '2px',
            backgroundColor: '#EEF1F6',
            color: '#2B3E4D',
            borderRadius: '8px',
            fontWeight: 600,
            fontSize: '0.8rem',
            fontFamily: 'Segoe UI, Roboto, sans-serif',
            textAlign: 'center',
            boxShadow: 'inset 0 0 0 1px rgba(0, 0, 0, 0.06)',
          }}
        >
          {displayIdx + 1}
        </Box>
      </Box>

      <Box sx={{ ...getGridCellSx('left'), overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {row.code}
      </Box>
      <Box sx={{ ...getGridCellSx('left'), overflow: 'hidden' }}>
        <Box sx={{ width: '100%', minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontSize: '0.86rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {row.description}
          </Typography>
          {hasPendingHint ? (
            <Tooltip
              placement="top-start"
              title={
                <Box sx={{ py: 0.2 }}>
                  {pendingByOrder.slice(0, 4).map((source) => (
                    <Typography key={source.order_id} variant="caption" sx={{ display: 'block' }}>
                      {`Pendiente por enviar OC ${source.order_code || source.order_id}: ${Number(source.pending_quantity || 0).toLocaleString('es-GT')}`}
                    </Typography>
                  ))}
                  {pendingByOrder.length > 4 ? (
                    <Typography variant="caption" sx={{ display: 'block' }}>
                      +{pendingByOrder.length - 4} OC adicionales
                    </Typography>
                  ) : null}
                </Box>
              }
            >
              <Box
                component="span"
                sx={{
                  mt: 0.28,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.35,
                  maxWidth: '100%',
                  color: 'warning.dark',
                  fontSize: '0.72rem',
                  fontWeight: 500,
                  bgcolor: 'warning.50',
                  border: '1px solid',
                  borderColor: 'warning.200',
                  px: 0.7,
                  py: '1px',
                  borderRadius: 1,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                <InfoOutlined sx={{ fontSize: 13 }} />
                {`Cantidad por despachar: ${pendingQty.toLocaleString('es-GT')}${pendingOrders > 1 ? ` (${pendingOrders} OC)` : ''}${pendingFormats.length ? ` · ${pendingFormats.join(', ')}` : ''}`}
              </Box>
            </Tooltip>
          ) : null}
        </Box>
      </Box>

      <Box sx={getGridCellSx('right')}>
        <TextField
          type="number"
          value={row.quantity !== 0 ? row.quantity : ''}
          onChange={(e) => onQuantityChange(rowIndex, e.target.value)}
          size="small"
          inputProps={{ min: 0 }}
          sx={{ width: '100%' }}
        />
      </Box>

      <Box sx={getGridCellSx('center')}>
        <FormControl size="small" sx={{ width: '100%' }}>
          <Select
            value={formatValue}
            onChange={(e) => onFormatChange(rowIndex, e.target.value)}
            displayEmpty
            inputProps={{ 'aria-label': 'Formato' }}
            renderValue={(selected) => {
              if (!selected) {
                return formatLabel ? formatLabel : <em>Seleccione</em>;
              }
              return formatsById.get(String(selected))?.name || formatLabel || selected;
            }}
            sx={{ width: '100%' }}
          >
            {(formats || []).map((f) => (
              <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box sx={getGridCellSx('right')}>
        <FormControl size="small" sx={{ width: '100%' }}>
          <Select
            value={row.listPrice || ''}
            onChange={(e) => onListPriceChange(rowIndex, e.target.value)}
            displayEmpty
            inputProps={{ 'aria-label': 'Precio cobrado' }}
            renderValue={(selected) => (!selected ? <em>Seleccione</em> : selected)}
            sx={{ width: '100%' }}
          >
            <MenuItem disabled value="">
              <em>Seleccione precio</em>
            </MenuItem>
            {row.prices && row.prices.map((priceItem) => (
              <MenuItem key={priceItem.id} value={priceItem.price}>
                {priceItem.price}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {hasPromotions && (
        <Box sx={getGridCellSx('right')}>
          <Box sx={{ position: 'relative', width: '100%' }}>
            <TextField
              type="number"
              value={row.bonusPrice !== undefined && row.bonusPrice !== 0 ? row.bonusPrice : ''}
              onChange={(e) => onBonusPriceChange(rowIndex, e.target.value)}
              onBlur={() => onTextFieldBlur(rowIndex, 'bonusPrice')}
              size="small"
              inputProps={{ min: 0 }}
              sx={{ width: '100%' }}
              placeholder="0.00"
              error={showPromoPriceWarning}
            />
            {showPromoPriceWarning && (
              <Tooltip title="Falta precio bonificado (si aplica)" placement="top">
                <Box
                  sx={{
                    position: 'absolute',
                    top: -6,
                    right: -6,
                    width: 16,
                    height: 16,
                    borderRadius: 99,
                    bgcolor: 'error.main',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '11px',
                    lineHeight: 1,
                  }}
                >
                  !
                </Box>
              </Tooltip>
            )}
          </Box>
        </Box>
      )}

      <Box sx={getGridCellSx('right')}>
        <TextField
          type="number"
          value={row.isFinalPriceModified ? row.finalPrice : ''}
          onChange={(e) => onPriceChange(rowIndex, e.target.value)}
          onBlur={() => onTextFieldBlur(rowIndex, 'finalPrice')}
          size="small"
          inputProps={{ min: 0 }}
          sx={{ width: '100%' }}
          placeholder={finalPlaceholder}
        />
      </Box>

      <Box sx={{ ...getGridCellSx('right'), gap: 0.5 }}>
        <IconButton color="error" onClick={() => onRemove(rowIndex)}>
          <Delete />
        </IconButton>
        <Tooltip title="Agregar promoción">
          <IconButton color="success" onClick={() => onAddPromo(rowIndex)}>
            <Discount />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
});

const BonusRow = React.memo(function BonusRow({
  style,
  ariaAttributes,
  gridTemplateColumns,
  displayIdx,
  bonus,
  bonusKey,
  hasPromotions,
  formats,
  formatsById,
  resolveFormatId,
  resolveFormatLabel,
  onBonusQuantityChange,
  onBonusFormatChange,
  onBonusReasonChange,
  onRemoveBonus,
}) {
  const formatValue = resolveFormatId(bonus) || '';
  const formatLabel = resolveFormatLabel(bonus);
  const reasonSpan = hasPromotions ? 2 : 1;
  const actionsStart = hasPromotions ? 8 : 7;

  return (
    <Box
      component="div"
      style={style}
      {...ariaAttributes}
      sx={{
        display: 'grid',
        gridTemplateColumns,
        alignItems: 'center',
        height: ROW_HEIGHT,
        borderBottom: '1px dashed rgba(0, 0, 0, 0.12)',
        color: 'success.main',
      }}
    >
      <Box sx={getGridCellSx('left')}>
        <Box
          sx={{
            display: 'inline-block',
            minWidth: '24px',
            px: 1,
            py: '2px',
            backgroundColor: 'rgba(76, 175, 80, 0.3)',
            color: '#1b5e20',
            borderRadius: '8px',
            fontWeight: 600,
            fontSize: '0.8rem',
            fontFamily: 'Segoe UI, Roboto, sans-serif',
            textAlign: 'center',
            boxShadow: 'inset 0 0 0 1px rgba(27, 94, 32, 0.2)',
          }}
        >
          {displayIdx + 1}
        </Box>
      </Box>

      <Box sx={{ ...getGridCellSx('left'), overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {bonus.code}
      </Box>

      <Box sx={{ ...getGridCellSx('left'), overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {bonus.description || 'Producto promocional'} (Promoción)
      </Box>

      <Box sx={getGridCellSx('right')}>
        <TextField
          size="small"
          type="number"
          value={bonus.quantity !== 0 ? bonus.quantity : ''}
          onChange={(e) => onBonusQuantityChange(bonusKey, e.target.value)}
          inputProps={{ min: 0 }}
          sx={{ width: '100%' }}
        />
      </Box>

      <Box sx={getGridCellSx('center')}>
        <FormControl size="small" sx={{ width: '100%' }}>
          <Select
            value={formatValue}
            onChange={(e) => onBonusFormatChange(bonusKey, e.target.value)}
            displayEmpty
            renderValue={(selected) => {
              if (!selected) return formatLabel ? formatLabel : <em>Seleccione</em>;
              return formatsById.get(String(selected))?.name || formatLabel || selected;
            }}
            sx={{ width: '100%' }}
          >
            {(formats || []).map((f) => (<MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>))}
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ ...getGridCellSx('left'), gridColumn: `6 / span ${reasonSpan}` }}>
        <TextField
          value={bonus.reason || ''}
          onChange={(e) => onBonusReasonChange(bonusKey, e.target.value)}
          size="small"
          fullWidth
          placeholder="Razón"
          sx={{ width: '100%' }}
        />
      </Box>

      <Box sx={{ ...getGridCellSx('right'), gridColumn: `${actionsStart} / span 2` }}>
        <IconButton size="small" color="error" onClick={() => onRemoveBonus(bonusKey)}>
          <Delete fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
});

export default function CustomTableOrder({
  setOpen,
  orderDetails,
  setOrderDetails,
  products,
  setProducts,
  setOpenPromotionModal,
  setCurrentOrderDetailIndex,
  order,
  setOrder,
  pendingProductsContext = [],
}) {
  const setOrderDetailsRef = React.useRef(setOrderDetails);
  const setOrderRef = React.useRef(setOrder);

  useEffect(() => {
    setOrderDetailsRef.current = setOrderDetails;
  }, [setOrderDetails]);

  useEffect(() => {
    setOrderRef.current = setOrder;
  }, [setOrder]);

  const [formats, setFormats] = React.useState([]);
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await API.get('catalogs/create_order/');
        const list = Array.isArray(data?.formats) ? data.formats : [];
        if (mounted) setFormats(list);
      } catch {
        if (mounted) setFormats([]);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const [searchTerm, setSearchTerm] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  const [showOnlyPromos, setShowOnlyPromos] = React.useState(false);
  const pendingByProductId = React.useMemo(() => {
    const map = new Map();
    (pendingProductsContext || []).forEach((ctx) => {
      const pid = Number(ctx?.product_id);
      if (Number.isFinite(pid)) {
        map.set(pid, ctx);
      }
    });
    return map;
  }, [pendingProductsContext]);


  useEffect(() => {
    if (!searchTerm) {
      setDebouncedSearch('');
      return;
    }
    const handle = setTimeout(() => setDebouncedSearch(searchTerm), 200);
    return () => clearTimeout(handle);
  }, [searchTerm]);

  const handleOpen = () => setOpen(true);

  const formatsById = React.useMemo(() => {
    const map = new Map();
    (formats || []).forEach((f) => {
      map.set(String(f.id), f);
    });
    return map;
  }, [formats]);

  const formatsByName = React.useMemo(() => {
    const map = new Map();
    (formats || []).forEach((f) => {
      const key = normalizeText(f.name);
      if (key) map.set(key, f);
    });
    return map;
  }, [formats]);

  const handleQuantityChange = React.useCallback((index, value) => {
    const numeric = value === '' ? '' : parseFloat(value);
    setOrderDetails(prev => prev.map((row, i) => (i === index ? { ...row, quantity: numeric } : row)));
  }, [setOrderDetails]);

  const handleFormatChange = React.useCallback((index, value) => {
    const normalizedValue = value || null;
    setOrderDetails(prev => prev.map((row, i) => (
      i === index
        ? ({
          ...row,
          prformat_id: normalizedValue,
          format_id: normalizedValue,
          prformat: formatsById.get(String(value)) || null,
        })
        : row
    )));
  }, [formatsById, setOrderDetails]);

  const resolveFormatId = React.useCallback((row) => {
    const direct = (
      row?.prformat_id ||
      row?.format_id ||
      row?.prformat?.id ||
      row?.product?.prformat_id ||
      row?.product?.prformat?.id ||
      row?.prformatId ||
      null
    );
    if (direct) return direct;
    const name = typeof row?.prformat === 'string'
      ? row.prformat
      : row?.prformat?.name || row?.prformat_name || row?.format_name || row?.product?.prformat?.name || '';
    if (!name) return '';
    const match = formatsByName.get(normalizeText(name));
    return match?.id || '';
  }, [formatsByName]);

  const resolveFormatLabel = React.useCallback((row) => {
    const id = resolveFormatId(row);
    if (id) {
      const match = formatsById.get(String(id));
      if (match?.name) return match.name;
    }
    const name = typeof row?.prformat === 'string'
      ? row.prformat
      : row?.prformat?.name || row?.prformat_name || row?.format_name || row?.product?.prformat?.name || '';
    return name || '';
  }, [formatsById, resolveFormatId]);

  useEffect(() => {
    if (!formats.length) return;
    setOrderDetailsRef.current((prev) => {
      let changed = false;
      const next = prev.map((row) => {
        if (row?.prformat_id || row?.format_id || row?.prformat?.id) return row;
        const name = typeof row?.prformat === 'string'
          ? row.prformat
          : row?.prformat?.name || row?.prformat_name || row?.format_name || row?.product?.prformat?.name || '';
        if (!name) return row;
        const match = formatsByName.get(normalizeText(name));
        if (!match) return row;
        changed = true;
        return { ...row, prformat_id: match.id, format_id: match.id, prformat: match };
      });
      return changed ? next : prev;
    });
  }, [formats, formatsByName]);

  useEffect(() => {
    if (!formats.length || !(order?.order_bonus || []).length) return;
    setOrderRef.current((prev) => {
      if (!prev || !Array.isArray(prev.order_bonus) || prev.order_bonus.length === 0) return prev;
      let changed = false;
      const nextBonuses = prev.order_bonus.map((bonus) => {
        if (!bonus) return bonus;
        if (bonus.prformat_id || bonus.format_id) return bonus;
        if (bonus.prformat?.id) {
          changed = true;
          return { ...bonus, prformat_id: bonus.prformat.id, format_id: bonus.prformat.id };
        }
        const name = typeof bonus.prformat === 'string'
          ? bonus.prformat
          : bonus?.prformat?.name || bonus?.prformat_name || bonus?.format_name || bonus?.product?.prformat?.name || '';
        if (!name) return bonus;
        const match = formatsByName.get(normalizeText(name));
        if (!match) return bonus;
        changed = true;
        return { ...bonus, prformat_id: match.id, format_id: match.id, prformat: match };
      });
      return changed ? { ...prev, order_bonus: nextBonuses } : prev;
    });
  }, [formats, formatsByName, order?.order_bonus]);

  const getFinalPriceValue = (row) => {
    if (row.isFinalPriceModified && row.finalPrice !== undefined) return row.finalPrice;
    if (row.isBonusPriceModified && row.bonusPrice !== undefined && row.bonusPrice !== 0) return row.bonusPrice;
    return row.listPrice;
  };

  const handleListPriceChange = React.useCallback((index, value) => {
    const numericValue = parseFloat(value);
    setOrderDetails(prev => prev.map((row, i) => {
      if (i !== index) return row;
      const nextRow = { ...row, listPrice: numericValue };
      if (!nextRow.isFinalPriceModified) nextRow.finalPrice = getFinalPriceValue(nextRow);
      return nextRow;
    }));
  }, [setOrderDetails]);

  const handleBonusPriceChange = React.useCallback((index, value) => {
    setOrderDetails(prev => {
      const updated = [...prev];
      const curr = updated[index];
      const bonusPrice = value === '' ? undefined : parseFloat(value);
      const isBonusPriceModified = value !== '' && !isNaN(value);
      let nextRow = { ...curr, bonusPrice, isBonusPriceModified };
      if (!nextRow.isFinalPriceModified) nextRow = { ...nextRow, finalPrice: getFinalPriceValue(nextRow) };
      updated[index] = nextRow;
      return updated;
    });
  }, [setOrderDetails]);

  const handlePriceChange = React.useCallback((index, value) => {
    setOrderDetails((prevOrderDetails) => {
      const updatedOrderDetails = [...prevOrderDetails];
      const numericValue = value === '' ? '' : parseFloat(value);
      let nextRow = { ...updatedOrderDetails[index], finalPrice: numericValue, isFinalPriceModified: numericValue !== '' };
      if (!nextRow.isBonusPriceModified && (numericValue === '' || numericValue === null)) {
        nextRow = { ...nextRow, finalPrice: nextRow.listPrice || 0, isFinalPriceModified: false };
      }
      updatedOrderDetails[index] = nextRow;
      return updatedOrderDetails;
    });
  }, [setOrderDetails]);

  const handleTextFieldBlur = React.useCallback((index, field) => {
    setOrderDetails((prevOrderDetails) => {
      const updatedOrderDetails = [...prevOrderDetails];
      let nextRow = { ...updatedOrderDetails[index] };

      if (field === 'finalPrice') {
        if (!nextRow.finalPrice || nextRow.finalPrice === 0) {
          nextRow = {
            ...nextRow,
            finalPrice: nextRow.isBonusPriceModified ? (nextRow.bonusPrice || 0) : (nextRow.listPrice || 0),
            isFinalPriceModified: false,
          };
        }
      }

      if (field === 'bonusPrice') {
        if (nextRow.bonusPrice === undefined || nextRow.bonusPrice === 0) {
          nextRow = { ...nextRow, bonusPrice: 0, isBonusPriceModified: false };
          if (!nextRow.isFinalPriceModified) nextRow = { ...nextRow, finalPrice: nextRow.listPrice || 0 };
        }
      }

      updatedOrderDetails[index] = nextRow;
      return updatedOrderDetails;
    });
  }, [setOrderDetails]);

  const handleRemoveProduct = React.useCallback((index) => {
    const updatedOrderDetails = orderDetails.filter((_, i) => i !== index);
    const removedProduct = orderDetails[index];

    const updatedProducts = products.map(product => {
      if (product.id === removedProduct.product_id) return { ...product, isAdd: false };
      return product;
    });

    setOrderDetails(updatedOrderDetails);
    setProducts(updatedProducts);
  }, [orderDetails, products, setOrderDetails, setProducts]);

  const handleAddPromotion = React.useCallback((index) => {
    setCurrentOrderDetailIndex(index);
    setOpenPromotionModal(true);
  }, [setCurrentOrderDetailIndex, setOpenPromotionModal]);

  const resolveBonusKey = React.useCallback((bonus, idx) => {
    if (bonus?.id != null) return bonus.id;
    if (bonus?.tempId != null) return bonus.tempId;
    return `idx-${idx}`;
  }, []);

  const handleBonusQuantityChange = React.useCallback((bonusId, value) => {
    setOrder(prev => ({
      ...prev,
      order_bonus: (prev.order_bonus || []).map((b, idx) => {
        const key = resolveBonusKey(b, idx);
        return key === bonusId ? { ...b, quantity: value === '' ? 0 : parseFloat(value) } : b;
      })
    }));
  }, [setOrder, resolveBonusKey]);

  const handleBonusFormatChange = React.useCallback((bonusId, value) => {
    const normalizedValue = value || null;
    setOrder(prev => ({
      ...prev,
      order_bonus: (prev.order_bonus || []).map((b, idx) => {
        const key = resolveBonusKey(b, idx);
        return key === bonusId
          ? {
              ...b,
              prformat_id: normalizedValue,
              format_id: normalizedValue,
              prformat: formatsById.get(String(value)) || null,
            }
          : b;
      })
    }));
  }, [formatsById, setOrder, resolveBonusKey]);

  const handleBonusReasonChange = React.useCallback((bonusId, value) => {
    setOrder(prev => ({
      ...prev,
      order_bonus: (prev.order_bonus || []).map((bonus, idx) => {
        const key = resolveBonusKey(bonus, idx);
        return key === bonusId ? { ...bonus, reason: value } : bonus;
      })
    }));
  }, [setOrder, resolveBonusKey]);

  const handleRemoveBonus = React.useCallback((bonusId) => {
    setOrder(prev => ({
      ...prev,
      order_bonus: (prev.order_bonus || []).filter((b, idx) => resolveBonusKey(b, idx) !== bonusId)
    }));
  }, [setOrder, resolveBonusKey]);

  const hasPromotions = (order?.order_bonus?.length || 0) > 0;

  // Memoized single-pass totals avoid multiple reduces on each change.
  const orderTotals = React.useMemo(() => {
    let total = 0;
    let subTotal = 0;
    let bonusDiscount = 0;
    let priceDiscount = 0;
    let totalQty = 0;

    (orderDetails || []).forEach((item) => {
      const qty = getSafeNumber(item.quantity);
      const listPrice = getSafeNumber(item.listPrice);
      const bonusPrice = getSafeNumber(item.bonusPrice);
      const finalPrice = getSafeNumber(item.finalPrice);
      totalQty += qty;

      total += listPrice * qty;

      const effectivePrice = (item.isFinalPriceModified || item.isBonusPriceModified)
        ? getSafeNumber(item.finalPrice || item.bonusPrice || item.listPrice || 0)
        : listPrice;
      subTotal += effectivePrice * qty;

      if (bonusPrice) {
        bonusDiscount += (listPrice - bonusPrice) * qty;
      }

      const baseDiscount = (listPrice - finalPrice) * qty;
      const itemBonusDiscount = bonusPrice ? (listPrice - bonusPrice) * qty : 0;
      priceDiscount += (baseDiscount - itemBonusDiscount);
    });

    return {
      total: parseFloat(total.toFixed(2)),
      subTotal: parseFloat(subTotal.toFixed(2)),
      bonusDiscount: parseFloat(bonusDiscount.toFixed(2)),
      priceDiscount: parseFloat(priceDiscount.toFixed(2)),
      totalQty: parseFloat(totalQty.toFixed(2)),
    };
  }, [orderDetails]);

  useEffect(() => {
    setOrderRef.current((prev) => {
      if (
        prev.total === orderTotals.total &&
        prev.subTotal === orderTotals.subTotal &&
        prev.bonusDiscount === orderTotals.bonusDiscount &&
        prev.priceDiscount === orderTotals.priceDiscount
      ) {
        return prev;
      }
      return {
        ...prev,
        total: orderTotals.total,
        subTotal: orderTotals.subTotal,
        bonusDiscount: orderTotals.bonusDiscount,
        priceDiscount: orderTotals.priceDiscount,
      };
    });
  }, [orderTotals]);

  // Detectar productos con promo ligada (para mostrar alerta en bonusPrice del producto)
  const productsWithPromo = React.useMemo(() => {
    const set = new Set();
    (order?.order_bonus || []).forEach(b => {
      if (b?.source_product_id != null) set.add(Number(b.source_product_id));
    });
    return set;
  }, [order?.order_bonus]);

  const productIndex = React.useMemo(() => {
    const mapped = (orderDetails || []).map((row, i) => ({
      type: 'product',
      row,
      index: i,
      search: buildSearchText(row?.code, row?.description, resolveFormatLabel(row)),
      sortKey: normalizeText(row?.description || ''),
    }));
    return mapped.sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  }, [orderDetails, resolveFormatLabel]);

  const promoIndex = React.useMemo(() => (
    (order?.order_bonus || []).map((bonus, i) => ({
      type: 'bonus',
      bonus,
      bonusIndex: i,
      search: buildSearchText(
        bonus?.code,
        bonus?.description || bonus?.reason,
        bonus?.prformat?.name || bonus?.prformat || bonus?.prformat_name
      ),
    }))
  ), [order?.order_bonus]);

  const searchTokens = React.useMemo(() => {
    const text = normalizeText(debouncedSearch);
    if (!text) return [];
    return text.split(/\s+/).filter(Boolean);
  }, [debouncedSearch]);

  const filteredRows = React.useMemo(() => {
    const tokens = searchTokens;
    const matches = (text) => {
      if (tokens.length === 0) return true;
      return tokens.every((tok) => text.includes(tok));
    };

    const productRows = showOnlyPromos ? [] : productIndex;
    const promoRows = hasPromotions ? promoIndex : [];

    if (tokens.length === 0) {
      return [...productRows, ...promoRows];
    }

    return [
      ...productRows.filter((item) => matches(item.search)),
      ...promoRows.filter((item) => matches(item.search)),
    ];
  }, [productIndex, promoIndex, searchTokens, showOnlyPromos, hasPromotions]);

  const listWidth = React.useMemo(() => {
    const baseWidth =
      COL_WIDTHS.index +
      COL_WIDTHS.code +
      COL_WIDTHS.description +
      COL_WIDTHS.quantity +
      COL_WIDTHS.format +
      COL_WIDTHS.price +
      COL_WIDTHS.final +
      COL_WIDTHS.actions;
    return hasPromotions ? baseWidth + COL_WIDTHS.bonus : baseWidth;
  }, [hasPromotions]);

  const gridTemplateColumns = React.useMemo(() => {
    const cols = [
      `${COL_WIDTHS.index}px`,
      `${COL_WIDTHS.code}px`,
      `minmax(${COL_WIDTHS.description}px, 1fr)`,
      `${COL_WIDTHS.quantity}px`,
      `${COL_WIDTHS.format}px`,
      `${COL_WIDTHS.price}px`,
    ];
    if (hasPromotions) cols.push(`${COL_WIDTHS.bonus}px`);
    cols.push(`${COL_WIDTHS.final}px`, `${COL_WIDTHS.actions}px`);
    return cols.join(' ');
  }, [hasPromotions]);

  const scrollbarSize = React.useMemo(() => getScrollbarSize(), []);

  const listHeight = React.useMemo(() => {
    const maxBodyHeight = Math.round((typeof window !== 'undefined' ? window.innerHeight : 800) * 0.62);
    const availableHeight = Math.max(maxBodyHeight - ROW_HEIGHT, ROW_HEIGHT);
    const rows = Math.max(filteredRows.length, 1);
    return Math.min(rows * ROW_HEIGHT, availableHeight);
  }, [filteredRows.length]);

  const rowData = React.useMemo(() => ({
    rows: filteredRows,
    hasPromotions,
    gridTemplateColumns,
    formats,
    formatsById,
    resolveFormatId,
    resolveFormatLabel,
    resolveBonusKey,
    onQuantityChange: handleQuantityChange,
    onFormatChange: handleFormatChange,
    onListPriceChange: handleListPriceChange,
    onBonusPriceChange: handleBonusPriceChange,
    onPriceChange: handlePriceChange,
    onTextFieldBlur: handleTextFieldBlur,
    onRemove: handleRemoveProduct,
    onAddPromo: handleAddPromotion,
    onBonusQuantityChange: handleBonusQuantityChange,
    onBonusFormatChange: handleBonusFormatChange,
    onBonusReasonChange: handleBonusReasonChange,
    onRemoveBonus: handleRemoveBonus,
    productsWithPromo,
    pendingByProductId,
  }), [
    filteredRows,
    hasPromotions,
    gridTemplateColumns,
    formats,
    formatsById,
    resolveFormatId,
    resolveFormatLabel,
    resolveBonusKey,
    handleQuantityChange,
    handleFormatChange,
    handleListPriceChange,
    handleBonusPriceChange,
    handlePriceChange,
    handleTextFieldBlur,
    handleRemoveProduct,
    handleAddPromotion,
    handleBonusQuantityChange,
    handleBonusFormatChange,
    handleBonusReasonChange,
    handleRemoveBonus,
    productsWithPromo,
    pendingByProductId,
  ]);

  const RowRenderer = React.useCallback((props) => {
    const { index, style, ariaAttributes, rows, ...data } = props;
    const item = rows[index];
    if (!item) return null;

    if (item.type === 'product') {
      const { row, index: rowIndex } = item;
      const pid = Number(row.product_id);
      const hasLinkedPromo = Number.isFinite(pid) && data.productsWithPromo.has(pid);
      const bonusEmpty = !(row.bonusPrice != null && Number(row.bonusPrice) > 0);
      const showPromoPriceWarning = data.hasPromotions && hasLinkedPromo && bonusEmpty;
      const pendingContext = Number.isFinite(pid) ? data.pendingByProductId.get(pid) : null;

      return (
        <ProductRow
          style={style}
          ariaAttributes={ariaAttributes}
          gridTemplateColumns={data.gridTemplateColumns}
          displayIdx={index}
          row={row}
          rowIndex={rowIndex}
          hasPromotions={data.hasPromotions}
          showPromoPriceWarning={showPromoPriceWarning}
          formats={data.formats}
          formatsById={data.formatsById}
          resolveFormatId={data.resolveFormatId}
          resolveFormatLabel={data.resolveFormatLabel}
          onQuantityChange={data.onQuantityChange}
          onFormatChange={data.onFormatChange}
          onListPriceChange={data.onListPriceChange}
          onBonusPriceChange={data.onBonusPriceChange}
          onPriceChange={data.onPriceChange}
          onTextFieldBlur={data.onTextFieldBlur}
          onRemove={data.onRemove}
          onAddPromo={data.onAddPromo}
          pendingContext={pendingContext}
        />
      );
    }

    const bonusKey = data.resolveBonusKey(item.bonus, item.bonusIndex);

    return (
      <BonusRow
        style={style}
        ariaAttributes={ariaAttributes}
        gridTemplateColumns={data.gridTemplateColumns}
        displayIdx={index}
        bonus={item.bonus}
        bonusKey={bonusKey}
        hasPromotions={data.hasPromotions}
        formats={data.formats}
        formatsById={data.formatsById}
        resolveFormatId={data.resolveFormatId}
        resolveFormatLabel={data.resolveFormatLabel}
        onBonusQuantityChange={data.onBonusQuantityChange}
        onBonusFormatChange={data.onBonusFormatChange}
        onBonusReasonChange={data.onBonusReasonChange}
        onRemoveBonus={data.onRemoveBonus}
      />
    );
  }, []);

  const money = (v) =>
    new Intl.NumberFormat('es-GT', { style: 'currency', currency: 'GTQ' }).format(Number(v || 0));

  return (
    <>
      {/* Top bar */}
      <Box sx={{
        p: 1.5,
        borderBottom: 1,
        borderColor: 'grey.200',
        backgroundColor: 'white'
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5
        }}>

          {/* Búsqueda minimalista */}
          <Box sx={{ flex: 1 }}>
            <TextField
              placeholder="Buscar..."
              variant="outlined"
              size="small"
              fullWidth
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" sx={{ color: 'grey.500' }} />
                  </InputAdornment>
                ),
                endAdornment: searchTerm ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      edge="end"
                      onClick={() => setSearchTerm('')}
                      sx={{ width: 24, height: 24 }}
                    >
                      <Close fontSize="small" sx={{ color: 'grey.500' }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
                sx: {
                  height: 36,
                  fontSize: '0.875rem',
                  backgroundColor: 'white'
                }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderColor: 'grey.300',
                  '&:hover': {
                    borderColor: 'grey.400'
                  }
                }
              }}
            />
          </Box>

          {/* Controles derechos compactos */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>

            {hasPromotions && (
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={showOnlyPromos}
                    onChange={(e) => setShowOnlyPromos(e.target.checked)}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
                    Promociones
                  </Typography>
                }
                sx={{ m: 0 }}
              />
            )}

            <Button
              variant="outlined"
              size="small"
              onClick={handleOpen}
              startIcon={<AddShoppingCart fontSize="small" />}
              sx={{
                height: 36,
                px: 2,
                fontSize: '0.8125rem',
                fontWeight: 500,
                textTransform: 'none',
                borderRadius: 0.75,
                borderColor: 'primary.main',
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.50',
                  borderColor: 'primary.dark'
                }
              }}
            >
              Agregar
            </Button>
          </Box>
        </Box>
      </Box>

      <Paper sx={{ height: '62vh', overflowX: 'auto', overflowY: 'hidden' }}>
        <Box sx={{ minWidth: listWidth }}>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns,
            background: '#10173c',
            minHeight: ROW_HEIGHT,
            alignItems: 'center',
            paddingRight: `${scrollbarSize}px`,
            boxSizing: 'border-box',
          }}>
            <Box sx={getHeaderCellSx('left')}>#</Box>
            <Box sx={getHeaderCellSx('left')}>Código</Box>
            <Box sx={getHeaderCellSx('left')}>Producto</Box>
            <Box sx={getHeaderCellSx('center')}>Cantidad</Box>
            <Box sx={getHeaderCellSx('center')}>Formato</Box>
            <Box sx={getHeaderCellSx('right')}>Precio cobrado</Box>
            {hasPromotions && (
              <Box sx={getHeaderCellSx('right')}>Precio bonificado</Box>
            )}
            <Box sx={getHeaderCellSx('right')}>Precio final</Box>
            <Box sx={getHeaderCellSx('right')}>Acciones</Box>
          </Box>

          {/* Body virtualizado: renderiza solo filas visibles */}
          <List
            rowCount={filteredRows.length}
            rowHeight={ROW_HEIGHT}
            rowComponent={RowRenderer}
            rowProps={rowData}
            style={{
              height: listHeight,
              width: '100%',
              overflowX: 'hidden',
              overflowY: 'auto',
              scrollbarGutter: 'stable',
            }}
          />
        </Box>
      </Paper>


      <Paper sx={{ mt: 1, p: 2, background: '#f5f5f5' }}>
        <Box display="flex" justifyContent="space-between">
          <Box sx={{ width: '40%', textAlign: 'right' }}>
            <Typography variant="subtitle2" sx={{ fontSize: '0.9rem' }}>
              Total productos:
            </Typography>
            <Typography variant="body2" sx={{ color: 'grey', fontSize: '0.9rem' }}>
              {orderTotals.totalQty}
            </Typography>
          </Box>

          <Box sx={{ width: '16%' }} />

          <Box sx={{ width: '32%', display: 'flex', justifyContent: 'flex-end' }}>
            <Box sx={{ textAlign: 'right', mr: 2 }}>
              <Typography variant="subtitle2" sx={{ fontSize: '0.9rem', mb: 1 }}>
                Subtotal:
              </Typography>
              <Typography variant="subtitle2" sx={{ fontSize: '0.9rem', mb: 1 }}>
                Descuento por producto:
              </Typography>
              {hasPromotions && (
                <Typography variant="subtitle2" sx={{ fontSize: '0.9rem', mb: 1 }}>
                  Descuento por promoción:
                </Typography>
              )}
              <Typography variant="subtitle2" sx={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
                Total a pagar:
              </Typography>
            </Box>

            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="body2" sx={{ fontSize: '0.9rem', mb: 1, marginTop: '2px' }}>
                {money(order.subTotal)}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.9rem', mb: 1, marginTop: '12px' }}>
                {money(order.priceDiscount)}
              </Typography>
              {hasPromotions && (
                <Typography variant="body2" sx={{ fontSize: '0.9rem', mb: 1, marginTop: '12px' }}>
                  {money(order.bonusDiscount)}
                </Typography>
              )}
              <Typography variant="body2" sx={{ fontSize: '0.9rem', fontWeight: 'bold', marginTop: '8px' }}>
                {money(order.total)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>
    </>
  );
}
